// @flow
import React, { Children, type Node, useState } from 'react';
import classnames from 'classnames';

import { ICONS } from 'Components/utils/icon/icons';
import ratioUtils from 'Components/utils/ratios/ratios.utils';

import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Icon } from 'Components/frameworks/icon/Icon';
import Image from 'Components/frameworks/image/Image';
import { TrackClick } from 'Tracking/track-click/TrackClick';

import { themes } from './ThumbnailNavigation.themes';

type Props = {
  children?: Node,
  className?: string,
  hasImageBorder?: boolean,
  label: string,
  link?: ?{
    trackingData?: TrackingData,
    url: string,
  },
  seeMoreLabel?: string,
  theme?: string,
  thumbnail: {
    imageUrl: string,
    ratioKey?: MediaRatios,
  },
};

export const ThumbnailNavigation = ({
  children,
  className,
  hasImageBorder,
  label,
  link,
  seeMoreLabel,
  theme = 'default',
  thumbnail,
}: Props) => {
  const [isHovering, setHovering] = useState(false);

  if (!thumbnail.imageUrl) {
    return null;
  }

  const hasLink = link?.url;

  const {
    thBody,
    thLabel,
    thLabelStyle,
    thLink,
    thRoot,
    thSeeMoreIcon,
    thSeeMoreIconSize,
    thSeeMoreLabel,
    thSeeMoreLabelStyle,
    thThumbnail,
  } = themes[theme]({
    hasImageBorder,
    hasLink,
    isHovering,
  });

  const onEnter = hasLink ? () => setHovering(true) : null;
  const onLeave = hasLink ? () => setHovering(false) : null;

  return (
    <div className={classnames(className, thRoot, 'image-responsive-cell')}>
      <TrackClick
        {...link}
        as={hasLink ? DynamicLink : 'div'}
        className={thLink}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onFocus={onEnter}
        onBlur={onLeave}
      >
        <Image
          {...thumbnail}
          className={thThumbnail}
          ratioKey={thumbnail.ratioKey || ratioUtils.XL}
        />
        {!!label && (
          <div className={thLabel} style={thLabelStyle}>
            {label}
          </div>
        )}
        {!!seeMoreLabel && (
          <div className={thSeeMoreLabel} style={thSeeMoreLabelStyle}>
            {seeMoreLabel}
            <Icon
              className={thSeeMoreIcon}
              hasRtl
              name={ICONS.BOLDER_ARROW_FORTH}
              width={thSeeMoreIconSize}
            />
          </div>
        )}
      </TrackClick>
      {!!children && !!Children.count(children) && <div className={thBody}>{children}</div>}
    </div>
  );
};
